/**
 * Contexto del ingreso en cada request de Aprobaciones (pedido Sol 02/09):
 * oficina + clapbase tal cual el sheet, stock del inventario en esa clapbase,
 * sugerencia de modelo por puesto (solo aviso) y alerta de envío a domicilio
 * cuando no hay oficina. Copy ES (misma convención que el resto de la tabla).
 */
import { toastError } from '@/lib/toast-error';
import { useState } from 'react';
import { Building2, CheckCircle2, ChevronDown, ChevronRight, Home, Laptop, Lightbulb, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { normalizeDeviceStatus } from '@/../../shared/inventory-status';
import type { ApprovalContext } from '@/../../shared/types';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/contexts/SharedAuthContext';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { Trans, useTranslation } from 'react-i18next';

const STATUS_CHIP: Record<string, string> = {
  'lista para entregar': 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
  'pending: revisar': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400',
  'pending: para reparar': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400',
  'pending: formateo': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400',
};

interface PanelProps {
  context: ApprovalContext;
  approvalId?: string;
  employeeEmail?: string | null;
  employeeName?: string | null;
}

export function ApprovalContextPanel({ context, approvalId, employeeEmail, employeeName }: PanelProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { stock, suggestion } = context;
  const place = context.clapbaseSheet || context.clapbaseLive || context.country || 'este ingreso';

  // Asignar desde stock: solo escritores de inventario (tech/people/admin)
  const { roles } = useAuth() as { roles?: string[] };
  const canAssign = !!employeeEmail && (roles ?? []).some((r) => ['tech', 'people', 'admin'].includes(r));
  const utils = trpc.useUtils();
  const assign = trpc.approvals.assignDeviceFromStock.useMutation({
    onSuccess: (r) => {
      toast.success(`${r.modelo} (${r.nroSerie}) asignada a ${employeeName || employeeEmail}`);
      utils.approvals.getApprovalsContext.invalidate();
    },
    onError: (err) => toastError(err, 'common.toast.no_se_pudo_asignar_el_equipo'),
  });

  return (
    <div className="space-y-1.5 rounded-md border bg-muted/30 px-3 py-2.5 text-xs">
      {/* Oficina + clapbase del sheet */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="font-medium">{context.officeLabel}</span>
        <span className="text-muted-foreground">
          · {t('approvals.approvalcontextpanel.en_el_sheet')} {context.clapbaseSheet ? `"${context.clapbaseSheet}"` : t('approvals.approvalcontextpanel.sin_clapbase_cargada')}
          {context.clapbaseLive && context.clapbaseLive !== context.clapbaseSheet && (
            <> · {t('approvals.approvalcontextpanel.community_map_live', { value: context.clapbaseLive })}</>
          )}
        </span>
      </div>

      {/* El ingreso ya tiene compu asignada */}
      {context.assignedToIngreso && (
        <div className="flex flex-wrap items-center gap-1.5 text-green-700 dark:text-green-400">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
          <span className="font-medium">{t('approvals.approvalcontextpanel.ya_tiene_asignada')}</span>
          <span>{context.assignedToIngreso.modelo}</span>
          <span className="font-mono text-muted-foreground">{context.assignedToIngreso.nroSerie}</span>
        </div>
      )}

      {/* Stock en la oficina */}
      <div>
        <button
          type="button"
          className="flex flex-wrap items-center gap-x-2 gap-y-1 text-left"
          onClick={() => stock.total > 0 && setExpanded(!expanded)}
        >
          <Laptop className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          {stock.total === 0 ? (
            <span className="text-muted-foreground">{t('approvals.approvalcontextpanel.sin_compus_para_place', { place })}</span>
          ) : (
            <>
              <span className={(stock.available ?? 0) > 0 ? 'font-medium text-green-700 dark:text-green-400' : 'font-medium'}>
                {t('approvals.approvalcontextpanel.disponibles_para_asignar', { count: stock.available ?? 0 })}
              </span>
              {stock.ready > (stock.available ?? 0) && (
                <span className="text-muted-foreground">· {stock.ready - (stock.available ?? 0)} {t('approvals.approvalcontextpanel.listas_pero_ya_asignadas')}</span>
              )}
              {stock.prep > 0 && (
                <span className="text-muted-foreground">· {stock.prep} {t('approvals.approvalcontextpanel.en_preparacion')}</span>
              )}
              {stock.total - stock.ready - stock.prep > 0 && (
                <span className="text-muted-foreground">· {stock.total - stock.ready - stock.prep} {t('approvals.approvalcontextpanel.en_otro_estado')}</span>
              )}
              {expanded ? <ChevronDown className="h-3 w-3 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 text-muted-foreground" />}
            </>
          )}
        </button>
        {expanded && stock.devices.length > 0 && (
          <ul className="mt-1.5 space-y-1 pl-5">
            {stock.devices.map((d) => (
              <li key={d.nroSerie || d.modelo} className="flex flex-wrap items-center gap-1.5">
                <span className="font-medium">{d.modelo || t('approvals.approvalcontextpanel.sin_modelo')}</span>
                {d.nroSerie && <span className="font-mono text-muted-foreground">{d.nroSerie}</span>}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                    STATUS_CHIP[normalizeDeviceStatus(d.status)] ??
                    'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                  }`}
                >
                  {d.status}
                </span>
                {!d.available && d.owner && (
                  <span className="text-[10px] text-muted-foreground">owner: {d.owner}</span>
                )}
                {d.available && canAssign && !context.assignedToIngreso && (
                  <button
                    type="button"
                    disabled={assign.isPending}
                    onClick={() => {
                      if (window.confirm(`¿Asignar ${d.modelo} (${d.nroSerie}) a ${employeeName || employeeEmail}?\n\nQueda como owner en el inventario, en "Lista para Entregar" para el board de entregas.`)) {
                        assign.mutate({ deviceId: d.deviceId, employeeEmail: employeeEmail!, employeeName: employeeName || undefined, approvalId });
                      }
                    }}
                    className="inline-flex items-center gap-1 rounded-full border border-green-300 bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-700 hover:bg-green-100 disabled:opacity-50 dark:border-green-700 dark:bg-green-900/20 dark:text-green-400"
                  >
                    {assign.isPending ? <WinclapLoader size={10} /> : <UserPlus className="h-3 w-3" />}
                    {t('approvals.approvalcontextpanel.asignar_a')} {employeeName?.split(' ')[0] || t('approvals.approvalcontextpanel.este_ingreso')}
                  </button>
                )}
              </li>
            ))}
            {stock.total > stock.devices.length && (
              <li className="text-muted-foreground">{t('approvals.approvalcontextpanel.y_n_mas_ver_inventario', { n: stock.total - stock.devices.length })}</li>
            )}
          </ul>
        )}
      </div>

      {/* Aviso de modelo por puesto (solo aviso) */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Lightbulb className="h-3.5 w-3.5 shrink-0 text-amber-500" />
        <span>
          {t('approvals.approvalcontextpanel.sugerencia')} <span className="font-semibold">{suggestion.model}</span>
          <span className="text-muted-foreground"> ({suggestion.reason}{suggestion.roleUsed ? t('approvals.approvalcontextpanel.rol_usado', { role: suggestion.roleUsed }) : ''})</span>
        </span>
      </div>

      {/* Sin oficina ⇒ envío a domicilio */}
      {!context.hasOffice && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          <Home className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <Trans i18nKey="approvals.approvalcontextpanel.sin_oficina_en_place" values={{ place }} components={{ b: <b /> }} />
          </span>
        </div>
      )}
    </div>
  );
}
